import React, { useEffect } from 'react'

import { useTeamStaleGroupIncidents } from '../hooks/useTeamStaleGroupIncidents'
import { useLightMode } from '@/hooks/useLightMode'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  Button,
  Card,
  CardCollapsibleRow,
  CardExpandable,
  CardHeader,
  Checkbox,
  Box as DarkBox,
  Text as DarkText,
  Skeleton,
  tokenPrefix,
} from '@hivewatch/dark'

import { tVar } from '@/utils/i18n'

import { TEAM_STALE_DETAILS_PAGE_SIZE } from '../constants/constants'
import type { TeamStaleGroup, TeamStaleIncident } from '../types/types'
import {
  getTeamAcknowledgedAt,
  getTeamGroupKey,
  getTeamUserName,
} from '../utils/utils'
import { StaleIncidentCard } from './StaleIncidentCard'

interface TeamStaleGroupSectionIProps {
  group: TeamStaleGroup
  /** The open snapshot's staleness cutoff, shared with the grouping query. */
  cutoff: string
  isExpanded: boolean
  onCardClick: (incident: TeamStaleIncident) => void
  onToggle: () => void
  /** Renders the bulk-resolve controls (ENG-32272, flag + role gated). */
  canBulkResolve?: boolean
  /** Selected incident ids across the whole modal; this section reads its own. */
  selectedIncidentIds?: Set<string>
  onToggleSelect?: (incidentId: string, isSelected: boolean) => void
  /** Select/clear this group's LOADED incidents (server paging, ENG-32213). */
  onSelectAll?: (incidentIds: string[], isSelected: boolean) => void
  /**
   * Deselect ids whose cards left the list without a bulk resolve (resolved
   * individually through the drawer, which opens over the modal), so the
   * ActionBar never counts rows that are no longer shown. Only sections see
   * incident data under the server paging, hence per-section reporting.
   */
  onSelectionPruned?: (incidentIds: string[]) => void
  /**
   * The group's last active incident is gone (drawer resolves remove cards
   * via the normalized cache, but the server aggregate row never reacts) —
   * the modal hides the group so the all-done state can be reached without
   * a refetch. May fire more than once; the parent treats it idempotently.
   */
  onEmptied?: () => void
}

/**
 * One expandable per-user section of the team stale work modal. The header
 * count is the server's per-user aggregate (ENG-32213); the incident cards
 * are paged server-side via the attribution filter, fetched lazily on first
 * expansion behind a "show more" button — a collapsed section costs zero
 * detail requests. The select-all checkbox covers the incidents loaded so
 * far (the full id list no longer exists client-side), so it only renders
 * once the section is expanded and its first page has landed — a collapsed
 * row offers no dead control.
 */
export const TeamStaleGroupSection = ({
  group,
  cutoff,
  isExpanded,
  onCardClick,
  onToggle,
  canBulkResolve = false,
  selectedIncidentIds,
  onToggleSelect,
  onSelectAll,
  onSelectionPruned,
  onEmptied,
}: TeamStaleGroupSectionIProps) => {
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  const groupKey = getTeamGroupKey(group)
  const {
    incidents,
    isLoading,
    isLoadingMore,
    hasMore,
    hasLoadedFirstPage,
    remainingCount,
    resolvedLoadedIds,
    loadMore,
    isError,
    retry,
  } = useTeamStaleGroupIncidents({
    cutoff,
    isExpanded,
    userId: group.userId ?? null,
  })

  // Report drawer-resolved loaded ids so the modal drops them from the
  // selection; the parent's updater is a no-op when none are selected.
  useEffect(() => {
    if (resolvedLoadedIds.length > 0) onSelectionPruned?.(resolvedLoadedIds)
  }, [onSelectionPruned, resolvedLoadedIds])

  // Report a settled, fully-loaded group with zero active cards:
  // hasLoadedFirstPage keeps a never-expanded section (query skipped, so
  // not loading and zero incidents) from reporting as emptied.
  const isEmptied =
    hasLoadedFirstPage &&
    !isLoading &&
    !isLoadingMore &&
    !isError &&
    !hasMore &&
    incidents.length === 0
  useEffect(() => {
    if (isEmptied) onEmptied?.()
  }, [isEmptied, onEmptied])

  // CardCollapsibleRow is click-only; make the header keyboard-operable
  // from this call site since it gates all of the section's content.
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onToggle()
    }
  }

  const loadedIncidentIds = incidents.map((incident) => incident.id)
  const selectedCount = loadedIncidentIds.filter((id) =>
    selectedIncidentIds?.has(id)
  ).length
  const areAllSelected =
    loadedIncidentIds.length > 0 && selectedCount === loadedIncidentIds.length

  // Two distinct null cases (ENG-32213): no attribution key is the
  // Unassigned bucket; an attribution key whose user did not resolve is a
  // user deleted from Keycloak, rendered as a former user. A resolved user
  // with blank first AND last name also falls through to the former-user
  // label — nameless accounts are not an expected state, so the conflation
  // is accepted over a fourth label for a cosmetic edge (ENG-32213 review).
  const userName = group.user
    ? getTeamUserName(group.user) || t('incidents:stale_team_former_user')
    : group.userId
    ? t('incidents:stale_team_former_user')
    : t('incidents:stale_team_unassigned')

  return (
    <Card colorScheme={colorScheme}>
      <CardHeader>
        <CardCollapsibleRow
          aria-expanded={isExpanded}
          colorScheme={colorScheme}
          data-testid={`team-stale-group-${groupKey}`}
          isExpanded={isExpanded}
          onKeyDown={handleKeyDown}
          onToggle={onToggle}
          role='button'
          tabIndex={0}
        >
          <DarkBox alignItems='center' display='flex' gap='8px'>
            {/* Nested control inside the click/keyboard-operable header
                row: stop propagation so operating it never toggles the
                section. */}
            {/* Selection needs loaded ids, which exist only after the first
                expansion's page lands — until then no checkbox renders at
                all, rather than a disabled control inviting a dead click. */}
            {canBulkResolve && isExpanded && loadedIncidentIds.length > 0 ? (
              <DarkBox
                onClick={(event: React.MouseEvent) => event.stopPropagation()}
                onKeyDown={(event: React.KeyboardEvent) =>
                  event.stopPropagation()
                }
              >
                <Checkbox
                  aria-label={tVar(t, 'incidents:stale_bulk_select_group', {
                    name: userName,
                  })}
                  checked={areAllSelected}
                  colorScheme={colorScheme}
                  data-testid={`team-stale-group-select-all-${groupKey}`}
                  indeterminate={selectedCount > 0 && !areAllSelected}
                  onChange={(event) =>
                    onSelectAll?.(loadedIncidentIds, event.target.checked)
                  }
                />
              </DarkBox>
            ) : null}
            <DarkText fontSize='14px'>
              {`${userName} (${group.incidentCount})`}
            </DarkText>
          </DarkBox>
        </CardCollapsibleRow>
        <CardExpandable isExpanded={isExpanded}>
          {/* Mounted only while expanded: CardExpandable keeps collapsed
              content in the DOM under aria-hidden, and the cards'
              tabIndex={0} would leave invisible tab stops (a WCAG
              violation). Costs the collapse-out animation, which resets on
              every open anyway. This is also what defers the details fetch:
              the hook above requests nothing until isExpanded is true. */}
          {isExpanded ? (
            <DarkBox display='flex' flexDirection='column' gap='8px'>
              {isLoading ? (
                <DarkBox
                  data-testid={`team-stale-group-loading-${groupKey}`}
                  py='4px'
                >
                  <Skeleton
                    bg={`${tokenPrefix(colorScheme)}bg.skeleton`}
                    borderRadius='4px'
                    height='16px'
                    width='60%'
                  />
                </DarkBox>
              ) : null}
              {isError ? (
                <>
                  <Alert
                    colorScheme={colorScheme}
                    title={t('common:error')}
                    variant='error'
                  />
                  <Button
                    colorScheme={colorScheme}
                    onClick={retry}
                    variant='secondary'
                  >
                    {t('common:retry')}
                  </Button>
                </>
              ) : null}
              {incidents.map((incident) => (
                <StaleIncidentCard
                  acknowledgedAt={getTeamAcknowledgedAt(incident)}
                  incident={incident}
                  isSelectable={canBulkResolve}
                  isSelected={Boolean(selectedIncidentIds?.has(incident.id))}
                  key={incident.id}
                  onClick={() => onCardClick(incident)}
                  onSelectChange={(isSelected) =>
                    onToggleSelect?.(incident.id, isSelected)
                  }
                />
              ))}
              {hasMore && !isLoading && !isError ? (
                <Button
                  colorScheme={colorScheme}
                  data-testid={`team-stale-group-load-more-${groupKey}`}
                  isLoading={isLoadingMore}
                  onClick={loadMore}
                  variant='secondary'
                >
                  {/* Capped at the page size: one click loads at most one
                      page, so a larger count would overpromise. */}
                  {tVar(t, 'incidents:stale_team_show_more', {
                    count: Math.min(
                      remainingCount,
                      TEAM_STALE_DETAILS_PAGE_SIZE
                    ),
                  })}
                </Button>
              ) : null}
            </DarkBox>
          ) : null}
        </CardExpandable>
      </CardHeader>
    </Card>
  )
}
